import { useCallback, useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { useConfirm } from "@/components/ConfirmDialog";
import { KebabMenu } from "@/components/KebabMenu";
import { SearchField } from "@/components/SearchField";
import { Toast, useToast } from "@/components/Toast";
import { useApi } from "@/lib/api";
import { hasRole } from "@/lib/hasRole";
import type { PagedResult } from "@/lib/pagination";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { ScimNotice } from "./ScimNotice";
import type { TeamLayoutContext } from "./TeamLayout";
import { EmptyState } from "@/components/EmptyState";
import { LoadError } from "@/components/LoadError";

const MAX_SUGGESTIONS = 8;

interface TeamMember {
  userId: string;
  name: string;
  email: string;
  avatarId: string | null;
}

interface Team {
  id: string;
  name: string;
  createdAt: string;
  members: TeamMember[];
  provisionedVia: number;
  isScimManaged: boolean;
}

interface OrgUser {
  id: string;
  name: string;
  email: string;
  avatarId: string | null;
}

function MemberSkeleton() {
  return (
    <div className="animate-pulse">
      {Array.from({ length: 4 }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-2.5 border-b border-surface/70 py-3"
        >
          <div className="h-7 w-7 shrink-0 rounded-full bg-surface" />
          <div>
            <div className="h-3 w-36 rounded bg-surface" />
            <div className="mt-1.5 h-2.5 w-48 rounded bg-surface" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function TeamMembers() {
  const { accessToken, user: signedInUser } = useAuth();
  const api = useApi();
  const { orgId, teamId } = useParams();
  const { onTeamChanged } = useOutletContext<TeamLayoutContext>();
  const [team, setTeam] = useState<Team | null>(null);
  const [searchResult, setSearchResult] = useState<{
    query: string;
    users: OrgUser[];
  } | null>(null);
  const { toast, showToast } = useToast();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 300);
  const [actioning, setActioning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = hasRole(signedInUser?.roles, "Admin");

  const fetchTeam = useCallback(async () => {
    if (!accessToken || !orgId || !teamId) {
      return null;
    }

    return api.get<Team>(`/organizations/${orgId}/teams/${teamId}`);
  }, [accessToken, api, orgId, teamId]);

  const applyTeam = useCallback((data: Team | null) => {
    if (data) {
      setTeam(data);
      setError(null);
    } else {
      setError("Couldn't load team.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchTeam().then((data) => {
      if (!cancelled) {
        applyTeam(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchTeam, applyTeam]);

  useEffect(() => {
    if (!accessToken || !orgId || !debouncedQuery || !isAdmin) {
      return;
    }

    let cancelled = false;

    const params = new URLSearchParams({
      page: "1",
      pageSize: String(MAX_SUGGESTIONS + 5),
      name: debouncedQuery,
    });

    void api
      .get<PagedResult<OrgUser>>(
        `/organizations/${orgId}/users?${params.toString()}`,
      )
      .then((data) => {
        if (!cancelled) {
          setSearchResult({ query: debouncedQuery, users: data?.items ?? [] });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, api, orgId, debouncedQuery, isAdmin]);

  function retry() {
    setError(null);
    setTeam(null);
    void fetchTeam().then(applyTeam);
  }

  async function handleAddMember(user: OrgUser) {
    if (!accessToken || !orgId || !teamId || !team) {
      return;
    }

    setActioning(true);

    try {
      await api.send(
        `/organizations/${orgId}/teams/${teamId}/members`,
        "POST",
        { userId: user.id },
        "Couldn't add member.",
      );

      showToast(`${user.name} added to ${team.name}.`, "success");
      setQuery("");
      applyTeam(await fetchTeam());
      onTeamChanged();
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Couldn't add member.",
        "error",
      );
    } finally {
      setActioning(false);
    }
  }

  async function handleRemoveMember(member: TeamMember) {
    if (!accessToken || !orgId || !teamId || !team) {
      return;
    }

    const confirmed = await confirm(
      `${member.name} will lose access to resources granted through this team.`,
      {
        title: `Remove ${member.name} from ${team.name}?`,
        confirmLabel: "Remove",
      },
    );

    if (!confirmed) {
      return;
    }

    setActioning(true);

    try {
      await api.send(
        `/organizations/${orgId}/teams/${teamId}/members/${member.userId}`,
        "DELETE",
        undefined,
        "Couldn't remove member.",
      );

      showToast(`${member.name} removed from ${team.name}.`, "success");
      applyTeam(await fetchTeam());
      onTeamChanged();
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Couldn't remove member.",
        "error",
      );
    } finally {
      setActioning(false);
    }
  }

  const isScimManaged = team?.isScimManaged ?? false;

  const matches =
    searchResult && searchResult.query === debouncedQuery
      ? searchResult.users
      : null;

  const suggestions = (matches ?? [])
    .filter((u) => !team?.members.some((m) => m.userId === u.id))
    .slice(0, MAX_SUGGESTIONS);

  return (
    <div>
      {error && !team && (
        <LoadError what="this team" onRetry={retry} className="mb-4" />
      )}

      {isScimManaged && (
        <div className="mb-4">
          <ScimNotice>
            This team is managed by your identity provider. Membership is set
            there.
          </ScimNotice>
        </div>
      )}

      {isAdmin && team && !isScimManaged && (
        <div className="mb-4">
          <SearchField
            value={query}
            onChange={setQuery}
            label="Search users to add to this team"
            placeholder="Search users to add"
            className="w-full"
          />

          {debouncedQuery && (
            <div className="mt-2 rounded-lg border border-muted bg-surface">
              {matches === null ? (
                <p className="px-3 py-2.5 text-sm text-foreground/70">
                  Searching...
                </p>
              ) : suggestions.length === 0 ? (
                <p className="px-3 py-2.5 text-sm text-foreground/70">
                  No users match "{debouncedQuery}" who aren't already in this
                  team.
                </p>
              ) : (
                suggestions.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between gap-3 border-b border-muted px-3 py-2 last:border-b-0"
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <Avatar
                        name={user.name}
                        email={user.email}
                        avatarId={user.avatarId}
                        size="md"
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm">{user.name}</div>
                        <div className="truncate text-xs text-foreground/70">
                          {user.email}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="primary"
                      onClick={() => handleAddMember(user)}
                      disabled={actioning}
                    >
                      Add
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {!team && !error ? (
        <MemberSkeleton />
      ) : team && team.members.length === 0 ? (
        <EmptyState
          title="No one in this team yet"
          description={
            isScimManaged
              ? "Membership comes from your identity provider."
              : "Members get everything this team's access rules grant."
          }
        />
      ) : (
        team && (
          <div>
            {[...team.members]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((member) => (
                <div
                  key={member.userId}
                  className="-mx-2 flex items-center justify-between gap-3 rounded border-b border-surface/70 px-2 py-2.5 hover:bg-surface"
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Avatar
                      name={member.name}
                      email={member.email}
                      avatarId={member.avatarId}
                      size="md"
                    />
                    <div className="min-w-0">
                      <div className="truncate text-sm">{member.name}</div>
                      <div className="truncate text-xs text-foreground/70">
                        {member.email}
                      </div>
                    </div>
                  </div>
                  {isAdmin && (
                    <KebabMenu
                      label={`More actions for ${member.name}`}
                      items={[
                        {
                          label: "Remove from team",
                          onClick: () => handleRemoveMember(member),
                          disabled: actioning || isScimManaged,
                          danger: true,
                        },
                      ]}
                    />
                  )}
                </div>
              ))}
          </div>
        )
      )}

      <Toast toast={toast} />

      {confirmDialog}
    </div>
  );
}
